import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  Button,
  ButtonRouterLink,
  Card,
  HoldToConfirm,
  Modal,
  SectionHead,
  Skeleton,
  TextField
} from '../components/primitives';
import {
  ORG_NAME_PATTERN,
  RESERVED_ORG_NAMES,
  useSettingsOrganizationVM
} from '../lib/vm/useSettingsOrganizationVM';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import './SettingsOrganization.css';

const NAME_TAKEN_ERROR = 'That name is taken. Try another.';
const INVALID_NAME_ERROR =
  'Use 2–40 letters, numbers, spaces, or hyphens. Reserved words not allowed.';
const NETWORK_ERROR = "Couldn't save. Check your connection and try again.";
const SUCCESS_MESSAGE = 'Updated';
const DELETE_NETWORK_ERROR = "Couldn't archive. Try again.";

/** What an org archive sweeps up — static copy per settings-organization
 *  prd.md OQ-3 (real per-resource counts aren't wired until the doc/task
 *  features ship). */
const ARCHIVE_BULLETS = [
  'All documents and tasks',
  'Every member of this organization',
  'Role rules and org standards',
  'Document types, custom attributes, permission groups, function roles',
  'Agent context loaded for this org'
];

/**
 * `/settings/organization` (turn 4.2b). Owners-only.
 *
 * Layout:
 *  1. SectionHead "Rename" — single TextField + Save/Cancel. Cross-tab
 *     broadcast on success.
 *  2. SectionHead "Transfer ownership" — single ButtonRouterLink to
 *     `/members?action=transfer` + blurb.
 *
 * Non-Owners (Admin / Member / null) hit a 403 surface — the page deliberately
 * does NOT mount the Rename form so a smuggled rpc call requires editing the
 * page. Server-side RPC also enforces 42501 for the same scenario.
 *
 * The Delete-org card lands in turn 4.3 (separate confirm + hold-to-confirm
 * flow); intentionally absent here.
 */
export function SettingsOrganization() {
  const vm = useSettingsOrganizationVM();

  return (
    <section
      className="set-org-page"
      data-testid="settings-organization"
      data-viewer-role={vm.viewerRole ?? ''}
      data-status={vm.status}
    >
      <SettingsBackLink />
      {vm.status === 'loading' ? (
        <div className="set-org-loading" data-testid="settings-org-loading">
          <Skeleton height={48} />
          <Skeleton height={160} />
          <Skeleton height={120} />
        </div>
      ) : null}

      {vm.status === 'error' ? (
        <Banner kind="error" data-testid="settings-org-error">
          {vm.error ?? "Couldn't load your organization. Try again."}
        </Banner>
      ) : null}

      {vm.status === 'unauth' ? (
        <Banner kind="default" data-testid="settings-org-unauth">
          You need to sign in to view organization settings.
        </Banner>
      ) : null}

      {vm.status === 'empty' ? (
        <Banner kind="default" data-testid="settings-org-empty">
          You don't belong to any org yet.
        </Banner>
      ) : null}

      {vm.status === 'forbidden' ? (
        <Banner kind="default" data-testid="settings-org-forbidden">
          Organization settings are restricted to Owners. Ask an Owner if you
          need a change here.
        </Banner>
      ) : null}

      {vm.status === 'ready' ? <ReadyView vm={vm} /> : null}
    </section>
  );
}

function ReadyView({
  vm
}: {
  vm: ReturnType<typeof useSettingsOrganizationVM>;
}) {
  const orgName = vm.orgName ?? '';
  const [draft, setDraft] = useState(orgName);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the draft synced when the server-truth name changes (post-save) so
  // a subsequent edit starts from the new name, not the stale one.
  useEffect(() => {
    setDraft(orgName);
  }, [orgName]);

  const trimmed = draft.trim();
  const dirty = trimmed !== orgName;
  const formatOk = !trimmed || ORG_NAME_PATTERN.test(trimmed);
  const reserved =
    !!trimmed && RESERVED_ORG_NAMES.has(trimmed.toLowerCase());

  const clientErrorText = useMemo(() => {
    if (!dirty) return null;
    if (!trimmed) return null; // empty field after edit — let the Save click surface it
    if (!formatOk) return INVALID_NAME_ERROR;
    if (reserved) return INVALID_NAME_ERROR;
    return null;
  }, [dirty, trimmed, formatOk, reserved]);

  const serverErrorText = useMemo(() => {
    if (!vm.saveRenameError) return null;
    if (vm.saveRenameError === 'name_taken') return NAME_TAKEN_ERROR;
    if (vm.saveRenameError === 'invalid_name') return INVALID_NAME_ERROR;
    return NETWORK_ERROR;
  }, [vm.saveRenameError]);

  // Client error takes precedence while the user is typing; once the user
  // submits and the server replies, the server error surfaces. A subsequent
  // keystroke clears the server error (clearError) so the field doesn't
  // misleadingly say "taken" against a name they've now edited.
  const errorText = clientErrorText ?? serverErrorText;

  const canSave =
    dirty &&
    formatOk &&
    !reserved &&
    !!trimmed &&
    vm.saveRenameStatus !== 'saving';

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    setDraft(e.target.value);
    if (vm.saveRenameError) vm.actions.clearError();
  }

  function onCancel() {
    setDraft(orgName);
    if (vm.saveRenameError) vm.actions.clearError();
    inputRef.current?.focus();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSave) return;
    await vm.actions.saveRename(trimmed);
  }

  const saving = vm.saveRenameStatus === 'saving';
  const showSuccess = vm.saveRenameStatus === 'success';

  return (
    <>
      <Card
        className="set-org-card"
        data-testid="settings-org-rename-card"
      >
        <SectionHead marker="01" title="Rename" tag="ORG · DISPLAY-ONLY" />
        <p className="set-org-blurb">
          Display-only — the org's id stays the same, so links and bookmarks
          keep working. Other tabs pick the new name up automatically.
        </p>
        <form
          className="set-org-form"
          onSubmit={onSubmit}
          data-testid="settings-org-rename-form"
        >
          <TextField
            ref={inputRef}
            id="settings-org-name"
            label="Organization name"
            value={draft}
            onChange={onChange}
            maxLength={40}
            autoComplete="off"
            spellCheck={false}
            errorText={errorText ?? undefined}
            data-testid="settings-org-name-input"
          />
          <div className="set-org-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={!canSave}
              data-testid="settings-org-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={!dirty || saving}
              data-testid="settings-org-cancel"
            >
              Cancel
            </Button>
            {showSuccess ? (
              <span
                className="set-org-success"
                role="status"
                data-testid="settings-org-success"
              >
                {SUCCESS_MESSAGE}
              </span>
            ) : null}
          </div>
        </form>
      </Card>

      <Card
        className="set-org-card"
        data-testid="settings-org-transfer-card"
      >
        <SectionHead
          marker="02"
          title="Transfer ownership"
          tag="OWNER · ROLE"
        />
        <p className="set-org-blurb">
          Move the Owner role to another member. The current Owner becomes
          Admin. Last-Owner protection and the target picker live on the
          Members page.
        </p>
        <div className="set-org-actions">
          <ButtonRouterLink
            to="/members?action=transfer"
            variant="default"
            data-testid="settings-org-transfer-cta"
          >
            Transfer ownership →
          </ButtonRouterLink>
        </div>
      </Card>

      <DeleteSection vm={vm} />
    </>
  );
}

function DeleteSection({
  vm
}: {
  vm: ReturnType<typeof useSettingsOrganizationVM>;
}) {
  const navigate = useNavigate();
  const orgName = vm.orgName ?? '';
  const [panelOpen, setPanelOpen] = useState(false);
  const [confirmDraft, setConfirmDraft] = useState('');

  const nameMatches = confirmDraft.trim() === orgName && orgName.length > 0;
  const deleting = vm.deleteOrgStatus === 'deleting';
  const showSuccess = vm.deleteOrgStatus === 'success';

  const deleteErrorText = useMemo(() => {
    if (vm.deleteOrgError === 'name_mismatch') {
      return 'That name doesn’t match. Check the spelling above.';
    }
    if (vm.deleteOrgError === 'network') return DELETE_NETWORK_ERROR;
    return null;
  }, [vm.deleteOrgError]);

  function openPanel() {
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setConfirmDraft('');
    if (vm.deleteOrgError) vm.actions.clearDeleteError();
  }

  function onConfirmChange(e: ChangeEvent<HTMLInputElement>) {
    setConfirmDraft(e.target.value);
    if (vm.deleteOrgError) vm.actions.clearDeleteError();
  }

  async function onHoldComplete() {
    if (!nameMatches || deleting) return;
    await vm.actions.deleteOrg(confirmDraft);
  }

  return (
    <>
      <Card
        className="set-org-card set-org-danger"
        data-testid="settings-org-delete-card"
      >
        <SectionHead marker="03" title="Danger zone" tag="ORG · DESTRUCTIVE" />
        <p className="set-org-blurb">
          Delete this organization. It's archived for 90 days — Penrose support
          can restore it during that window. Members are emailed and lose
          access immediately.
        </p>
        {!panelOpen ? (
          <div className="set-org-actions">
            <Button
              type="button"
              variant="default"
              onClick={openPanel}
              data-testid="settings-org-delete-trigger"
            >
              Delete org
            </Button>
          </div>
        ) : (
          <div
            className="set-org-delete-confirm"
            data-testid="settings-org-delete-confirm"
          >
            <h4 className="set-org-delete-heading">Delete {orgName}</h4>
            <p className="set-org-blurb">
              This archives the organization and everything inside it:
            </p>
            <ul className="set-org-delete-list">
              {ARCHIVE_BULLETS.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
            <p className="set-org-blurb">
              Members will receive an email and lose access immediately.
              Penrose support can restore the org within 90 days.
            </p>
            <TextField
              id="settings-org-confirm-name"
              label={`Type "${orgName}" to confirm`}
              value={confirmDraft}
              onChange={onConfirmChange}
              placeholder={orgName}
              autoComplete="off"
              spellCheck={false}
              data-testid="settings-org-confirm-input"
            />
            <div className="set-org-actions set-org-delete-actions">
              <Button
                type="button"
                variant="ghost"
                onClick={closePanel}
                disabled={deleting}
                data-testid="settings-org-delete-cancel"
              >
                Cancel
              </Button>
              <HoldToConfirm
                onConfirm={onHoldComplete}
                disabled={!nameMatches || deleting}
                label={deleting ? 'Archiving…' : 'Delete (hold 5s)'}
                holdingLabel="Keep holding…"
                dataTestId="settings-org-delete-hold"
              />
            </div>
            {deleteErrorText ? (
              <span
                className="set-org-delete-error"
                role="alert"
                data-testid="settings-org-delete-error"
              >
                {deleteErrorText}
              </span>
            ) : null}
          </div>
        )}
      </Card>

      {showSuccess ? (
        <Modal
          open
          onClose={() => navigate('/settings')}
          title={`${vm.deletedOrgName ?? orgName} archived`}
          dataTestId="settings-org-delete-success"
          footer={
            <Button
              type="button"
              variant="primary"
              onClick={() => navigate('/settings')}
              data-testid="settings-org-delete-continue"
            >
              Continue
            </Button>
          }
        >
          Members have been notified by email. Contact Penrose support within
          90 days to recover this organization.
        </Modal>
      ) : null}
    </>
  );
}
